import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, MessageCircle, ShoppingBag, Tag } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";

export default function KosherPage() {
  const { t, tl, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => { if (location.key && location.key !== "default") navigate(-1); else navigate("/"); };
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: items = [] } = useQuery({
    queryKey: ["kosher-items"],
    queryFn: async () => (await api.get("/kosher-items")).data,
  });
  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const store = settings.kosher || {};
  const title = (lang !== "en" && store[`title_${lang}`]) || store.title || t("kosher.default_title");
  const info = (lang !== "en" && store[`info_${lang}`]) || store.info || "";
  const waNumber = (store.whatsapp_number || "").replace(/[^0-9]/g, "");
  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hi! I have a question about the kosher store.")}`;

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="kosher-page">
      <div className="grain" />
      <Navbar />

      {/* Hero — photo with soft dark gradient, matching the other program pages */}
      <SectionShell id="kosher-sec-1" label="Section 1" noReorder>
      <section ref={ref} className="relative pt-24 sm:pt-28">
        <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
          {store.image_url ? (
            <motion.img
              style={{ y: imgY }}
              src={resolveImg(store.image_url)}
              alt={title}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[#0F172A]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-[#0F172A]/10" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-7xl w-full px-5 sm:px-8 pb-10">
              <button onClick={goBack} data-testid="kosher-back" className="flex w-fit items-center gap-2 text-sm text-white/80 hover:text-white mb-5 transition-colors">
                <ArrowLeft size={16} /> <EditableText k="kosher.back" as="span" />
              </button>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]">
                <ShoppingBag size={14} /> <EditableText k="kosher.eyebrow" as="span" />
              </span>
              <h1 className="mt-3 font-serif text-4xl sm:text-6xl tracking-tight text-white leading-[0.98] max-w-3xl">{title}</h1>
            </div>
          </div>
        </div>
      </section>
      </SectionShell>

      {/* Info + WhatsApp */}
      {(info || waNumber) && (
        <SectionShell id="kosher-sec-2" label="Section 2" noReorder>
        <section className="relative z-10 py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              {info && <p className="text-[#0F172A] text-lg sm:text-xl leading-relaxed whitespace-pre-line">{info}</p>}
              {waNumber && (
                <a
                  href={waLink}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="kosher-whatsapp-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
                >
                  <MessageCircle size={18} /> <EditableText k="kosher.contact" as="span" />
                </a>
              )}
            </Reveal>
          </div>
        </section>
        </SectionShell>
      )}

      {/* Current specials */}
      <SectionShell id="kosher-sec-3" label="Section 3" noReorder>
      <section className="relative z-10 pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-10"><EditableText k="kosher.sales_title" as="span" /></h2>
          </Reveal>

          {items.length === 0 ? (
            <p data-testid="kosher-empty" className="text-[#475569]"><EditableText k="kosher.empty" as="span" /></p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((it, i) => (
                <Reveal key={it.id} delay={(i % 3) * 0.06}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    data-testid={`kosher-card-${it.id}`}
                    className="group h-full rounded-3xl bg-white border border-[#0F172A]/5 overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.06)] flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden">
                      {it.image_url ? (
                        <img src={resolveImg(it.image_url)} alt={tl(it, "name")} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] to-[#1e293b] flex items-center justify-center text-[#BE923C]/40"><ShoppingBag size={40} /></div>
                      )}
                      {it.on_sale && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#BE923C] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 shadow">
                          <Tag size={12} /> <EditableText k="kosher.on_sale" as="span" />
                        </span>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-serif text-xl leading-tight">{tl(it, "name")}</h3>
                        {tl(it, "price") && <span className="shrink-0 font-semibold text-[#BE923C]">{tl(it, "price")}</span>}
                      </div>
                      {tl(it, "description") && <p className="mt-2 text-sm text-[#475569] leading-relaxed">{tl(it, "description")}</p>}
                    </div>
                  </motion.div>
                </Reveal>
              ))}
            </div>
          )}

          {waNumber && (
            <div className="mt-14 text-center">
              <a
                href={waLink}
                target="_blank" rel="noopener noreferrer"
                data-testid="kosher-whatsapp-btn-bottom"
                className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
              >
                <MessageCircle size={18} /> <EditableText k="kosher.contact" as="span" />
              </a>
            </div>
          )}
        </div>
      </section>
      </SectionShell>

      <Footer />
    </div>
  );
}
